import React, {useContext, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {useFlags} from 'hooks';
import {LoggedInNavBar, LoggedOutNavBar, OnboardingNavBar} from 'components/nav';
import {GridContainer} from 'components';
import Show from 'components/utils/Show';
import MCSSnackbar from './MCSSnackbar';
import {PSingleLine} from 'components/TypographyShortcuts';
import {daysTilNextPayment} from './payment/NextPayment';
import {UserContext} from 'context/UserContext';
import {AppCtx} from 'context/AppContext';

const drawerWidth = '220px';

const useNavStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        minHeight: '100%',
    },
    // necessary for content to be below app bar
    toolbar: theme.mixins.toolbar,
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        width: '100%',
        [theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${drawerWidth})`,
        },
    },
}));

const userType = state => {
    switch (state) {
        case 'isUser':
        case 'notOnboarded':
            return 'loggedIn';
        case 'notOnTestList':
        case 'suspended':
            return 'onboarding';
        default:
            return 'loggedOut';
    }
};

function ResponsiveNav(props) {
    const classes = useNavStyles();
    const {payment_missing_snackbar_active} = useFlags();
    const {
        userState,
        user,
        hasBillingAddress,
        hasPaymentToken,
        subscription,
        organization_info,
    } = useContext(UserContext);
    const {snackOpen, snackProps, setSnackOpen, setSnackProps} = useContext(AppCtx);

    const showSnackbar = () =>
        userState === 'isUser' &&
        !(
            // all the ways user doesn't need payment info to use site
            // some role gave them ability canAccessWithoutPaymentInfo
            (
                user?.permissions?.canAccessWithoutPaymentInfo ||
                // they are an active member of an organization
                // if we change this to .length then will need to know when
                //   no longer able to use for free
                organization_info?.member_orgs?.some(
                    membership => membership.status === 'active'
                ) ||
                // they are managing any orgs
                organization_info?.managing_orgs?.length
            )
        ) &&
        payment_missing_snackbar_active &&
        daysTilNextPayment(subscription?.next_payment_date) < 3 &&
        (!hasBillingAddress || !hasPaymentToken);

    useEffect(() => {
        if (showSnackbar()) {
            let message = '';

            if (!hasBillingAddress) {
                message = 'billing address';
            } else if (!hasPaymentToken) {
                message = 'payment card';
            }

            setSnackProps({
                duration: null,
                severity: 'warning',
                message: (
                    <PSingleLine>
                        We are missing information from you! Please go to the{' '}
                        <Link to='/settings/payment'>payments page</Link> to enter
                        your {message} to keep using the app.
                    </PSingleLine>
                ),
            });
            setSnackOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState, user, payment_missing_snackbar_active, subscription]);

    return (
        <div className={classes.root}>
            <Show condition={userType(userState) === 'loggedIn'}>
                <LoggedInNavBar />
            </Show>
            <Show condition={userType(userState) === 'onboarding'}>
                <OnboardingNavBar />
            </Show>
            <Show condition={userType(userState) === 'loggedOut'}>
                <LoggedOutNavBar />
            </Show>
            <main className={classes.content}>
                <div className={classes.toolbar} />
                <GridContainer flexGrow={1}>{props.children}</GridContainer>
                <MCSSnackbar
                    open={snackOpen}
                    onClose={() => setSnackOpen(false)}
                    message={snackProps.message}
                    severity={snackProps.severity}
                    duration={snackProps.duration}
                />
            </main>
        </div>
    );
}

export default ResponsiveNav;
export {drawerWidth};
